import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  Users,
  MapPin,
  Calendar,
  Clock,
  Plus,
  Filter,
  ChevronRight,
  MessageCircle,
  UserPlus,
  Check,
  X,
  Target,
  TrendingUp,
  Loader2,
  Flag,
  Send
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { format, parseISO } from "date-fns";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Play style options
const PLAY_STYLES = [
  { value: "casual", label: "Casual", description: "Relaxed rounds, no pressure" },
  { value: "competitive", label: "Competitive", description: "Playing for skins or matches" },
  { value: "mentor", label: "Mentor", description: "Happy to help newer golfers" },
  { value: "aspiring", label: "Aspiring", description: "Looking to improve with better players" }
];

// Handicap range options
const HANDICAP_RANGES = [
  { value: "any", label: "Any Handicap" },
  { value: "0-10", label: "Scratch to 10", min: 0, max: 10 },
  { value: "10-20", label: "10 to 20", min: 10, max: 20 },
  { value: "20-30", label: "20 to 30", min: 20, max: 30 },
  { value: "30+", label: "30+", min: 30, max: 54 }
];

// Helper to safely extract error message from API responses
const getErrorMessage = (error, fallback = "An error occurred") => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ') || fallback;
  }
  return fallback;
};

const FindGolfersHub = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "find";
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [milestones, setMilestones] = useState(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [golfers, setGolfers] = useState([]);
  const [searchFilters, setSearchFilters] = useState({
    handicapRange: "any",
    playStyle: "",
    location: ""
  });

  // Open Tee Times state
  const [teeTimes, setTeeTimes] = useState([]);
  const [showPostTeeTime, setShowPostTeeTime] = useState(false);
  const [newTeeTime, setNewTeeTime] = useState({
    course_name: "",
    tee_time: "",
    spots_needed: 1,
    handicap_range: "",
    message: "",
    play_style: "casual"
  });
  const [postingTeeTime, setPostingTeeTime] = useState(false);

  // My activity state
  const [myPosts, setMyPosts] = useState([]);
  const [myResponses, setMyResponses] = useState([]);

  // Check milestones
  const checkMilestones = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/milestones/status`);
      setMilestones(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching milestones:", error);
      return null;
    }
  }, []);

  // Search golfers
  const searchGolfers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append("q", searchQuery);
      if (searchFilters.location) params.append("location", searchFilters.location);
      if (searchFilters.playStyle) params.append("play_style", searchFilters.playStyle);
      
      if (searchFilters.handicapRange && searchFilters.handicapRange !== "any") {
        const range = HANDICAP_RANGES.find(r => r.value === searchFilters.handicapRange);
        if (range) {
          params.append("handicap_min", range.min);
          params.append("handicap_max", range.max);
        }
      }

      const response = await axios.get(`${API}/golfers/search?${params.toString()}`);
      setGolfers(response.data.golfers || []);
    } catch (error) {
      console.error("Error searching golfers:", error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, searchFilters]);

  // Fetch open tee times
  const fetchTeeTimes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/open-tee-times`);
      setTeeTimes(response.data.tee_times || []);
    } catch (error) {
      console.error("Error fetching tee times:", error);
    }
  }, []);

  // Fetch my posts
  const fetchMyPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/open-tee-times/my-posts`);
      setMyPosts(response.data.posts || []);
    } catch (error) {
      console.error("Error fetching my posts:", error);
    }
  }, []);

  // Fetch my responses
  const fetchMyResponses = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/open-tee-times/my-responses`);
      setMyResponses(response.data.tee_times || []);
    } catch (error) {
      console.error("Error fetching my responses:", error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const init = async () => {
      const ms = await checkMilestones();
      if (ms && !ms.find_golfers_unlocked) {
        toast.error(`Complete ${ms.rounds_to_handicap} more round(s) to unlock Find Golfers`);
        navigate("/home");
        return;
      }
      
      searchGolfers();
      fetchTeeTimes();
      fetchMyPosts();
      fetchMyResponses();
    };
    init();
  }, [checkMilestones, searchGolfers, fetchTeeTimes, fetchMyPosts, fetchMyResponses, navigate]);

  // Re-search when filters change
  useEffect(() => {
    if (milestones?.find_golfers_unlocked) {
      searchGolfers();
    }
  }, [searchFilters, milestones, searchGolfers]);

  // Update URL when tab changes
  useEffect(() => {
    setSearchParams({ tab: activeTab });
  }, [activeTab, setSearchParams]);

  // Post tee time
  const handlePostTeeTime = async () => {
    if (!newTeeTime.course_name || !newTeeTime.tee_time) {
      toast.error("Please fill in course and tee time");
      return;
    }

    try {
      setPostingTeeTime(true);
      await axios.post(`${API}/open-tee-times`, newTeeTime);
      toast.success("Tee time posted!");
      setShowPostTeeTime(false);
      setNewTeeTime({
        course_name: "",
        tee_time: "",
        spots_needed: 1,
        handicap_range: "",
        message: "",
        play_style: "casual"
      });
      fetchTeeTimes();
      fetchMyPosts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to post tee time"));
    } finally {
      setPostingTeeTime(false);
    }
  };

  // Respond to tee time
  const handleRespondToTeeTime = async (teeTimeId) => {
    try {
      await axios.post(`${API}/open-tee-times/${teeTimeId}/respond`);
      toast.success("Request sent!");
      fetchTeeTimes();
      fetchMyResponses();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to send request"));
    }
  };

  // Send buddy request
  const handleSendBuddyRequest = async (targetUserId) => {
    try {
      await axios.post(`${API}/golf-buddies/request`, { target_user_id: targetUserId });
      toast.success("Buddy request sent!");
      searchGolfers();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to send request"));
    }
  };

  // Start conversation
  const handleStartConversation = async (targetUserId) => {
    try {
      const response = await axios.post(`${API}/conversations`, {
        participant_ids: [targetUserId]
      });
      navigate(`/chat?conversation=${response.data.conversation_id}`);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to start conversation"));
    }
  };

  if (!milestones?.find_golfers_unlocked) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <Card className="bg-gray-900 border-gray-800 max-w-md">
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-800 flex items-center justify-center">
              <Users className="w-8 h-8 text-gray-500" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Find Golfers Locked</h2>
            <p className="text-gray-400 mb-4">
              Complete {milestones?.rounds_to_handicap ?? 3} more round(s) to unlock this feature
              and connect with golfers in your area.
            </p>
            <Button onClick={() => navigate("/add-score")} className="bg-emerald-600 hover:bg-emerald-700">
              Log a Round
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] pb-24">
      {/* Header */}
      <div className="bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] pt-6 pb-4 px-4">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold text-white mb-2">Find Golfers</h1>
          <p className="text-gray-400">Connect with players and fill your tee times</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4">
        <div className="max-w-2xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 bg-gray-900 border border-gray-800 p-1 rounded-lg">
              <TabsTrigger 
                value="find" 
                className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-md"
                data-testid="tab-find-golfers"
              >
                <Search className="w-4 h-4 mr-2" />
                Find
              </TabsTrigger>
              <TabsTrigger 
                value="tee-times"
                className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-md"
                data-testid="tab-tee-times"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Tee Times
              </TabsTrigger>
              <TabsTrigger 
                value="activity"
                className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-md"
                data-testid="tab-activity"
              >
                <Users className="w-4 h-4 mr-2" />
                Activity
              </TabsTrigger>
            </TabsList>

            {/* Find Golfers Tab */}
            <TabsContent value="find" className="mt-4">
              {/* Search Bar */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    placeholder="Search by name, location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && searchGolfers()}
                    className="pl-10 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
                    data-testid="golfer-search-input"
                  />
                </div>
              </div>

              {/* Filters */}
              <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                <Select
                  value={searchFilters.handicapRange}
                  onValueChange={(v) => setSearchFilters(prev => ({ ...prev, handicapRange: v }))}
                >
                  <SelectTrigger className="w-[140px] bg-gray-900 border-gray-700 text-white">
                    <SelectValue placeholder="Handicap" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    {HANDICAP_RANGES.map(range => (
                      <SelectItem key={range.value} value={range.value} className="text-white">
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={searchFilters.playStyle || "any"}
                  onValueChange={(v) => setSearchFilters(prev => ({ ...prev, playStyle: v === "any" ? "" : v }))}
                >
                  <SelectTrigger className="w-[140px] bg-gray-900 border-gray-700 text-white">
                    <SelectValue placeholder="Play Style" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    <SelectItem value="any" className="text-white">Any Style</SelectItem>
                    {PLAY_STYLES.map(style => (
                      <SelectItem key={style.value} value={style.value} className="text-white">
                        {style.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Input
                  placeholder="Location..."
                  value={searchFilters.location}
                  onChange={(e) => setSearchFilters(prev => ({ ...prev, location: e.target.value }))}
                  className="w-[140px] bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
                />
              </div>

              {/* Golfers List */}
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : golfers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                  <p className="text-gray-400">No golfers found matching your criteria</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {golfers.map((golfer) => (
                    <GolferCard
                      key={golfer.user_id}
                      golfer={golfer}
                      currentUserId={user?.user_id}
                      onConnect={() => handleSendBuddyRequest(golfer.user_id)}
                      onMessage={() => handleStartConversation(golfer.user_id)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Open Tee Times Tab */}
            <TabsContent value="tee-times" className="mt-4">
              {/* Post CTA */}
              <Button
                onClick={() => setShowPostTeeTime(true)}
                className="w-full mb-4 bg-emerald-600 hover:bg-emerald-700 h-12"
                data-testid="post-tee-time-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Post Open Tee Time
              </Button>

              {/* Tee Times List */}
              {teeTimes.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                  <p className="text-gray-400 mb-2">No open tee times yet</p>
                  <p className="text-gray-500 text-sm">Be the first to post!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {teeTimes.map((tt) => (
                    <TeeTimeCard
                      key={tt.tee_time_id}
                      teeTime={tt}
                      currentUserId={user?.user_id}
                      onRespond={() => handleRespondToTeeTime(tt.tee_time_id)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Activity Tab */}
            <TabsContent value="activity" className="mt-4">
              {/* My Posts */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-3">My Tee Time Posts</h3>
                {myPosts.length === 0 ? (
                  <p className="text-gray-500 text-sm">You haven't posted any tee times yet</p>
                ) : (
                  <div className="space-y-3">
                    {myPosts.map((post) => (
                      <MyTeeTimePost 
                        key={post.tee_time_id} 
                        post={post}
                        onAccept={(responderId) => {
                          axios.post(`${API}/open-tee-times/${post.tee_time_id}/accept/${responderId}`)
                            .then(() => {
                              toast.success("Golfer accepted!");
                              fetchMyPosts();
                            })
                            .catch(() => toast.error("Failed to accept"));
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* My Responses */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">My Responses</h3>
                {myResponses.length === 0 ? (
                  <p className="text-gray-500 text-sm">You haven't responded to any tee times yet</p>
                ) : (
                  <div className="space-y-3">
                    {myResponses.map((tt) => (
                      <MyResponseCard key={tt.tee_time_id} teeTime={tt} />
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Post Tee Time Dialog */}
      <Dialog open={showPostTeeTime} onOpenChange={setShowPostTeeTime}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Post Open Tee Time</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300">Course Name *</Label>
              <Input
                placeholder="e.g., Pebble Beach"
                value={newTeeTime.course_name}
                onChange={(e) => setNewTeeTime(prev => ({ ...prev, course_name: e.target.value }))}
                className="mt-1 bg-gray-800 border-gray-700 text-white"
                data-testid="tee-time-course-input"
              />
            </div>

            <div>
              <Label className="text-gray-300">Tee Time *</Label>
              <Input
                type="datetime-local"
                value={newTeeTime.tee_time}
                onChange={(e) => setNewTeeTime(prev => ({ ...prev, tee_time: e.target.value }))}
                className="mt-1 bg-gray-800 border-gray-700 text-white"
                data-testid="tee-time-datetime-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Spots Needed</Label>
                <Select
                  value={newTeeTime.spots_needed.toString()}
                  onValueChange={(v) => setNewTeeTime(prev => ({ ...prev, spots_needed: parseInt(v) }))}
                >
                  <SelectTrigger className="mt-1 bg-gray-800 border-gray-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    {[1, 2, 3].map(n => (
                      <SelectItem key={n} value={n.toString()} className="text-white">
                        {n} player{n > 1 ? "s" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-gray-300">Play Style</Label>
                <Select
                  value={newTeeTime.play_style}
                  onValueChange={(v) => setNewTeeTime(prev => ({ ...prev, play_style: v }))}
                >
                  <SelectTrigger className="mt-1 bg-gray-800 border-gray-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    {PLAY_STYLES.map(style => (
                      <SelectItem key={style.value} value={style.value} className="text-white">
                        {style.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-gray-300">Preferred Handicap Range (optional)</Label>
              <Input
                placeholder="e.g., 10-20"
                value={newTeeTime.handicap_range}
                onChange={(e) => setNewTeeTime(prev => ({ ...prev, handicap_range: e.target.value }))}
                className="mt-1 bg-gray-800 border-gray-700 text-white"
              />
            </div>

            <div>
              <Label className="text-gray-300">Message (optional)</Label>
              <Textarea
                placeholder="Looking for a fun round, no betting..."
                value={newTeeTime.message}
                onChange={(e) => setNewTeeTime(prev => ({ ...prev, message: e.target.value }))}
                className="mt-1 bg-gray-800 border-gray-700 text-white"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPostTeeTime(false)} className="border-gray-700 text-gray-300">
              Cancel
            </Button>
            <Button
              onClick={handlePostTeeTime}
              disabled={postingTeeTime}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="submit-tee-time-btn"
            >
              {postingTeeTime ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Post Tee Time
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Golfer Card Component
const GolferCard = ({ golfer, currentUserId, onConnect, onMessage }) => {
  const isConnected = golfer.connection_status === "accepted";
  const isPending = golfer.connection_status === "pending";

  return (
    <Card className="bg-gray-900/50 border-gray-800" data-testid={`golfer-card-${golfer.user_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <Avatar className="w-12 h-12">
            <AvatarImage src={golfer.picture} />
            <AvatarFallback className="bg-emerald-500/20 text-emerald-400">
              {(golfer.username || golfer.name || "G")[0].toUpperCase()}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-white truncate">
                {golfer.username || golfer.name || "Golfer"}
              </span>
              {golfer.handicap != null && typeof golfer.handicap === 'number' && (
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-medium">
                  {golfer.handicap.toFixed(1)} HCP
                </span>
              )}
            </div>

            <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
              {golfer.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {golfer.location}
                </span>
              )}
              {golfer.rounds_count > 0 && (
                <span className="flex items-center gap-1">
                  <Flag className="w-3 h-3" />
                  {golfer.rounds_count} {golfer.rounds_count === 1 ? 'round' : 'rounds'}
                </span>
              )}
            </div>

            {golfer.bio && (
              <p className="text-sm text-gray-500 mt-2 line-clamp-2">{golfer.bio}</p>
            )}
          </div>

          <div className="flex gap-2">
            {isConnected ? (
              <Button
                size="sm"
                onClick={onMessage}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <MessageCircle className="w-4 h-4" />
              </Button>
            ) : isPending ? (
              <Button size="sm" disabled className="bg-gray-700">
                <Clock className="w-4 h-4 mr-1" />
                Pending
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={onConnect}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid={`connect-${golfer.user_id}`}
              >
                <UserPlus className="w-4 h-4 mr-1" />
                Connect
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Tee Time Card Component
const TeeTimeCard = ({ teeTime, currentUserId, onRespond }) => {
  const isOwner = teeTime.user_id === currentUserId;
  const hasResponded = teeTime.responses?.some(r => r.user_id === currentUserId);
  const teeTimeDate = teeTime.tee_time ? parseISO(teeTime.tee_time) : null;

  return (
    <Card className="bg-gray-900/50 border-gray-800" data-testid={`tee-time-card-${teeTime.tee_time_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <Avatar className="w-10 h-10">
            <AvatarImage src={teeTime.user_picture} />
            <AvatarFallback className="bg-blue-500/20 text-blue-400">
              {(teeTime.user_name || "G")[0].toUpperCase()}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <span className="font-medium text-white">{teeTime.user_name}</span>
              {teeTime.user_handicap != null && typeof teeTime.user_handicap === 'number' && (
                <span className="text-xs text-gray-400">{teeTime.user_handicap.toFixed(1)} HCP</span>
              )}
            </div>

            <div className="mt-2 p-3 bg-gray-800/50 rounded-lg">
              <div className="flex items-center gap-2 text-white font-medium">
                <Flag className="w-4 h-4 text-emerald-400" />
                {teeTime.course_name}
              </div>
              
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {teeTimeDate ? format(teeTimeDate, "MMM d") : "TBD"}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {teeTimeDate ? format(teeTimeDate, "h:mm a") : "TBD"}
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {teeTime.spots_needed - teeTime.spots_filled} spot{teeTime.spots_needed - teeTime.spots_filled !== 1 ? "s" : ""} left
                </span>
              </div>

              {teeTime.message && (
                <p className="mt-2 text-sm text-gray-300">{teeTime.message}</p>
              )}

              <div className="flex items-center gap-2 mt-2">
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  teeTime.play_style === "casual" ? "bg-blue-500/20 text-blue-400" :
                  teeTime.play_style === "competitive" ? "bg-orange-500/20 text-orange-400" :
                  teeTime.play_style === "mentor" ? "bg-purple-500/20 text-purple-400" :
                  "bg-emerald-500/20 text-emerald-400"
                }`}>
                  {PLAY_STYLES.find(s => s.value === teeTime.play_style)?.label || teeTime.play_style}
                </span>
                {teeTime.handicap_range && (
                  <span className="px-2 py-0.5 rounded-full bg-gray-700 text-gray-300 text-xs">
                    {teeTime.handicap_range} HCP
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {!isOwner && (
          <div className="mt-3 flex justify-end">
            {hasResponded ? (
              <Button size="sm" disabled className="bg-gray-700">
                <Check className="w-4 h-4 mr-1" />
                Request Sent
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={onRespond}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid={`respond-${teeTime.tee_time_id}`}
              >
                <Send className="w-4 h-4 mr-1" />
                Request to Join
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// My Tee Time Post Component
const MyTeeTimePost = ({ post, onAccept }) => {
  const pendingResponses = post.responses?.filter(r => r.status === "pending") || [];
  const acceptedResponses = post.responses?.filter(r => r.status === "accepted") || [];
  const teeTimeDate = post.tee_time ? parseISO(post.tee_time) : null;

  return (
    <Card className="bg-gray-900/50 border-gray-800" data-testid={`my-tee-time-post-${post.tee_time_id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="font-medium text-white">{post.course_name}</span>
            <div className="text-sm text-gray-400">
              {teeTimeDate ? format(teeTimeDate, "MMM d, h:mm a") : "TBD"}
            </div>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs ${
            post.status === "open" ? "bg-emerald-500/20 text-emerald-400" :
            post.status === "filled" ? "bg-blue-500/20 text-blue-400" :
            "bg-gray-700 text-gray-400"
          }`}>
            {post.status === "open" ? `${post.spots_needed - post.spots_filled} spots left` :
             post.status === "filled" ? "Filled" : post.status}
          </span>
        </div>

        {/* Accepted Players */}
        {acceptedResponses.length > 0 && (
          <div className="mb-3">
            <div className="text-xs text-gray-500 mb-2">Confirmed Players</div>
            <div className="space-y-2">
              {acceptedResponses.map((response) => (
                <div key={response.user_id} className="flex items-center gap-2 p-2 bg-emerald-500/10 rounded-lg">
                  <Avatar className="w-6 h-6">
                    <AvatarImage src={response.user_picture} />
                    <AvatarFallback className="text-xs">{response.user_name?.[0]}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm text-white">{response.user_name}</span>
                  <Check className="w-4 h-4 text-emerald-400 ml-auto" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Requests */}
        {pendingResponses.length > 0 && (
          <div>
            <div className="text-xs text-gray-500 mb-2">Pending Requests</div>
            <div className="space-y-2">
              {pendingResponses.map((response) => (
                <div key={response.user_id} className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg">
                  <Avatar className="w-6 h-6">
                    <AvatarImage src={response.user_picture} />
                    <AvatarFallback className="text-xs">{response.user_name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <span className="text-sm text-white">{response.user_name}</span>
                    {response.user_handicap != null && typeof response.user_handicap === 'number' && (
                      <span className="text-xs text-gray-400 ml-2">
                        {response.user_handicap.toFixed(1)} HCP
                      </span>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => onAccept(response.user_id)}
                    className="bg-emerald-600 hover:bg-emerald-700 h-7 px-2"
                  >
                    Accept
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {pendingResponses.length === 0 && acceptedResponses.length === 0 && (
          <p className="text-sm text-gray-500">No responses yet</p>
        )}
      </CardContent>
    </Card>
  );
};

// My Response Card Component
const MyResponseCard = ({ teeTime }) => {
  const status = teeTime.my_response_status;
  const teeTimeDate = teeTime.tee_time ? parseISO(teeTime.tee_time) : null;

  return (
    <Card className="bg-gray-900/50 border-gray-800">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <span className="font-medium text-white">{teeTime.course_name}</span>
            <div className="text-sm text-gray-400">
              {teeTimeDate ? format(teeTimeDate, "MMM d, h:mm a") : "TBD"} • Posted by {teeTime.user_name}
            </div>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs ${
            status === "accepted" ? "bg-emerald-500/20 text-emerald-400" :
            status === "pending" ? "bg-yellow-500/20 text-yellow-400" :
            "bg-red-500/20 text-red-400"
          }`}>
            {status === "accepted" ? "Confirmed" :
             status === "pending" ? "Awaiting Response" : "Declined"}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default FindGolfersHub;
